import { useCallback, useEffect, useRef } from 'react';
import type { SignalRNotification } from './useSignalR';
import { useToast } from '../components/UI/ToastProvider';
import {
  INBOX_TOAST_DEBOUNCE_MS,
  INBOX_TOAST_DURATION_MS,
  resolveInboxNotificationText,
  resolveInboxToastMessage,
} from '../utils/notificationToastPolicy';

export function useInboxNotificationToasts()
{
  const { addToast } = useToast();
  const pendingCountRef = useRef(0);
  const pendingTextsRef = useRef<string[]>([]);
  const pendingActionUrlsRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushInboxToasts = useCallback(() => {
    const count = pendingCountRef.current;
    const texts = pendingTextsRef.current.slice();
    const actionUrls = pendingActionUrlsRef.current.slice();
    pendingCountRef.current = 0;
    pendingTextsRef.current = [];
    pendingActionUrlsRef.current = [];
    timerRef.current = null;

    if (count <= 0)
    {
      return;
    }

    const actionUrl = count === 1 ? actionUrls[0]?.trim() : undefined;
    addToast(
      resolveInboxToastMessage(count, texts),
      'info',
      INBOX_TOAST_DURATION_MS,
      actionUrl && actionUrl.length > 0 ? actionUrl : undefined
    );
  }, [addToast]);

  const queueInboxToast = useCallback((notification: SignalRNotification) => {
    pendingCountRef.current += 1;
    pendingTextsRef.current.push(
      resolveInboxNotificationText(notification.message, notification.title)
    );
    pendingActionUrlsRef.current.push(notification.actionUrl ?? '');

    if (timerRef.current != null)
    {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(flushInboxToasts, INBOX_TOAST_DEBOUNCE_MS);
  }, [flushInboxToasts]);

  useEffect(() => {
    return () => {
      if (timerRef.current != null)
      {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { queueInboxToast };
}
